import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ShieldAlert, Clock, AlertTriangle, ArrowUpRight, Layers, MapPin, Repeat, PhoneCall, MailWarning, Info } from "lucide-react";

const C = { navy: "#1C4358", slate: "#4B5563", charcoal: "#252221", line: "#E6EDF2", red: "#9C1E22", amber: "#B7791F", green: "#2F7D5A" };

const RANGES = [
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
];

function Stat({ icon: Icon, label, value, suffix, tone = C.navy, hint, testid }) {
  const empty = value === null || value === undefined;
  return (
    <div className="rounded-xl border p-3.5" style={{ borderColor: C.line }} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11.5px] mb-1" style={{ color: C.slate }}>
        {Icon && <Icon size={13} />} {label}
      </div>
      <div className="text-2xl font-bold tabular-nums" style={{ color: empty ? C.slate : tone }}>
        {empty ? "—" : value}{!empty && suffix ? <span className="text-sm font-medium ml-0.5">{suffix}</span> : null}
      </div>
      {hint && <div className="text-[11px] mt-0.5" style={{ color: C.slate }}>{hint}</div>}
    </div>
  );
}

function BreakdownCard({ icon: Icon, title, note, rows, empty, rowTestid, containerTestid, render }) {
  return (
    <Card className="p-4 border-[#E6EDF2]" data-testid={containerTestid}>
      <div className="flex items-center gap-1.5 mb-2.5">
        {Icon && <Icon size={15} style={{ color: C.navy }} />}
        <span className="text-[13.5px] font-semibold" style={{ color: C.charcoal }}>{title}</span>
      </div>
      {note && <div className="text-[11px] mb-2 flex items-center gap-1" style={{ color: C.slate }}><Info size={11} /> {note}</div>}
      {(!rows || rows.length === 0) ? (
        <div className="text-[12.5px] py-1.5" style={{ color: C.slate }}>{empty || "Not enough data yet."}</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={i} data-testid={rowTestid} className="flex items-center justify-between gap-3 rounded-lg border px-2.5 py-1.5" style={{ borderColor: C.line }}>
              {render(r)}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function EscalationHealth() {
  const navigate = useNavigate();
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/communications/escalation-health", { params: { range } })
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range]);

  const ack = data?.avg_ack_minutes;
  const ackLabel = ack == null ? null : (ack >= 60 ? `${(ack / 60).toFixed(1)} hr` : `${ack} min`);

  return (
    <div className="max-w-5xl mx-auto pb-16" data-testid="escalation-health-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2" style={{ color: C.charcoal }}>
          <ShieldAlert size={22} style={{ color: C.red }} /> Escalation Health
        </h1>
        <Button variant="outline" size="sm" onClick={() => navigate("/settings/notification-routing")}
          className="h-8 border-[#D6E0E7] text-[#1C4358]" data-testid="eh-open-routing">Notification routing</Button>
      </div>
      <p className="text-[13px] mb-4" style={{ color: C.slate }}>
        Are urgent communications acknowledged on time, and which branches/tiers repeatedly breach SLA?
        Aggregate operational metrics only — no client details. {data?.scope === "scoped" && <span style={{ color: C.amber }}>Scoped to your branches.</span>}
      </p>

      {/* Range filter */}
      <div className="inline-flex rounded-lg border p-0.5 mb-5" style={{ borderColor: C.line }}>
        {RANGES.map((r) => (
          <button key={r.key} data-testid={`eh-range-${r.key}`} onClick={() => setRange(r.key)}
            className="px-3 h-8 text-[12.5px] rounded-md transition-colors"
            style={{ background: range === r.key ? C.navy : "transparent", color: range === r.key ? "#fff" : C.slate }}>
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3"><Skeleton className="h-24" /><Skeleton className="h-40" /></div>
      ) : !data?.has_data ? (
        <Card className="p-8 border-[#E6EDF2] text-center" data-testid="eh-empty">
          <ShieldAlert size={28} className="mx-auto mb-2" style={{ color: C.slate }} />
          <div className="text-[14px] font-semibold" style={{ color: C.charcoal }}>Not enough data yet.</div>
          <div className="text-[12.5px] mt-1" style={{ color: C.slate }}>No escalation activity in this window. Try a wider date range.</div>
        </Card>
      ) : (
        <div className="space-y-5">
          {/* Top-line stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            <Stat icon={Clock} label="Avg time to acknowledge" value={ackLabel} tone={C.navy}
              hint={data.acknowledged_count ? `${data.acknowledged_count} acknowledged` : "Awaiting reads"} testid="eh-avg-ack" />
            <Stat icon={AlertTriangle} label="% breaching SLA" value={data.breaching_pct} suffix="%" tone={C.red}
              hint={data.total_urgent_high ? `${data.sla_escalations}/${data.total_urgent_high} urgent+high` : "No urgent/high items"} testid="eh-breaching-pct" />
            <Stat icon={AlertTriangle} label="Urgent breaching" value={data.urgent_breaches} tone={C.red} testid="eh-urgent-breaches" />
            <Stat icon={AlertTriangle} label="High-priority breaching" value={data.high_breaches} tone={C.amber} testid="eh-high-breaches" />
            <Stat icon={ArrowUpRight} label="Central-admin fallback" value={data.central_fallback} tone={C.amber} testid="eh-central-fallback" />
            <Stat icon={ShieldAlert} label="Awaiting assigned owner" value={data.assigned_awaiting} tone={C.amber}
              hint="Past threshold, not yet escalated" testid="eh-assigned-awaiting" />
            <Stat icon={PhoneCall} label="Call-back tasks created" value={data.phone_tasks} tone={C.navy}
              hint="From escalations (internal tasks)" testid="eh-phone-tasks" />
            <Stat icon={MailWarning} label="Failed deliveries" value={data.failed_deliveries} tone={C.amber} testid="eh-failed-deliveries" />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <BreakdownCard icon={Layers} title="Escalations by tier" containerTestid="eh-by-tier" rowTestid="eh-tier-row"
              note="Assigned = currently past threshold, not yet escalated" rows={data.by_tier}
              render={(r) => (<>
                <span className="text-[12.5px]" style={{ color: C.charcoal }}>{r.label}{r.current ? "" : ""}</span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: r.current ? C.amber : C.red }}>{r.count}</span>
              </>)} />

            <BreakdownCard icon={Repeat} title="Repeat queues (2+ escalations)" containerTestid="eh-repeat-queues" rowTestid="eh-repeat-row"
              rows={data.repeat_queues} empty="No repeat-offending queues — good."
              render={(r) => (<>
                <span className="text-[12.5px]" style={{ color: C.charcoal }}>{r.branch} · <span style={{ color: C.slate }}>{r.type}</span></span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: C.red }}>{r.count}</span>
              </>)} />

            <BreakdownCard icon={MapPin} title="Escalations by branch" containerTestid="eh-by-branch" rowTestid="eh-branch-row"
              rows={data.by_branch} render={(r) => (<>
                <span className="text-[12.5px]" style={{ color: C.charcoal }}>{r.name}</span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: C.navy }}>{r.count}</span>
              </>)} />

            <BreakdownCard icon={MapPin} title="Escalations by territory" containerTestid="eh-by-territory" rowTestid="eh-territory-row"
              rows={data.by_territory} render={(r) => (<>
                <span className="text-[12.5px]" style={{ color: C.charcoal }}>{r.name}</span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: C.navy }}>{r.count}</span>
              </>)} />
          </div>
        </div>
      )}
    </div>
  );
}
